import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Buildings, SignOut, GearSix, House, WhatsappLogo, BellRinging, ArrowClockwise } from "@phosphor-icons/react";
import { api, getSecret, clearSecret, adminHeaders } from "@/lib/api";
import { RoomCard } from "@/components/admin/RoomCard";
import { CctvSettings } from "@/components/admin/CctvSettings";
import { WaSettings } from "@/components/admin/WaSettings";
import { ReminderSettings } from "@/components/admin/ReminderSettings";
import { SecretGate } from "@/components/admin/SecretGate";
export default function AdminPage() {
    const [authed, setAuthed] = useState(false);
    const [checking, setChecking] = useState(true);
    const [properties, setProperties] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [rooms, setRooms] = useState([]);
    const [newProp, setNewProp] = useState("");
    const [newRoom, setNewRoom] = useState("");
    const [addingProp, setAddingProp] = useState(false);
    const [showCctvSettings, setShowCctvSettings] = useState(false);
    const [showWaSettings, setShowWaSettings] = useState(false);
    const [showReminders, setShowReminders] = useState(false);

    const loadProperties = useCallback(async () => {
        const res = await api.get("/admin/properties", { headers: adminHeaders() });
        setProperties(res.data);
        setSelectedId((prev) => prev || (res.data[0] ? res.data[0].id : null));
    }, []);

    const loadRooms = useCallback(async (propId) => {
        if (!propId) {
            setRooms([]);
            return;
        }
        const res = await api.get(`/admin/properties/${propId}/rooms`, { headers: adminHeaders() });
        setRooms(res.data);
    }, []);

    useEffect(() => {
        const saved = getSecret();
        if (!saved) {
            setChecking(false);
            return;
        }
        api.post("/admin/verify", { phrase: saved })
            .then(() => setAuthed(true))
            .catch(() => clearSecret())
            .finally(() => setChecking(false));
    }, []);

    useEffect(() => {
        if (authed) loadProperties().catch(() => toast.error("Gagal memuat properti"));
    }, [authed, loadProperties]);

    useEffect(() => {
        if (authed && selectedId) loadRooms(selectedId).catch(() => toast.error("Gagal memuat ruangan"));
    }, [authed, selectedId, loadRooms]);

    const addProperty = async (e) => {
        e.preventDefault();
        if (!newProp.trim()) return;
        setAddingProp(true);
        try {
            const res = await api.post("/admin/properties", { name: newProp }, { headers: adminHeaders() });
            setNewProp("");
            await loadProperties();
            setSelectedId(res.data.id);
            toast.success("Properti ditambahkan");
        } catch {
            toast.error("Gagal menambah properti");
        } finally {
            setAddingProp(false);
        }
    };

    const addRoom = async (e) => {
        e.preventDefault();
        if (!newRoom.trim() || !selectedId) return;
        try {
            await api.post(`/admin/properties/${selectedId}/rooms`, { name: newRoom }, { headers: adminHeaders() });
            setNewRoom("");
            await loadRooms(selectedId);
            await loadProperties();
            toast.success("Ruangan ditambahkan");
        } catch {
            toast.error("Gagal menambah ruangan");
        }
    };

    const deleteProperty = async (id) => {
        if (!window.confirm("Hapus properti beserta seluruh ruangannya?")) return;
        try {
            await api.delete(`/admin/properties/${id}`, { headers: adminHeaders() });
            if (selectedId === id) setSelectedId(null);
            await loadProperties();
            toast.success("Properti dihapus");
        } catch {
            toast.error("Gagal menghapus properti");
        }
    };

    const refreshRooms = async () => {
        await loadRooms(selectedId);
        await loadProperties();
    };

    if (checking) return null;
    if (!authed) return <SecretGate onSuccess={() => setAuthed(true)} />;

    const selected = properties.find((p) => p.id === selectedId);

    return (
        <div className="min-h-screen flex flex-col md:flex-row" data-testid="admin-dashboard">
            <aside className="md:w-72 border-b md:border-b-0 md:border-r border-border bg-white md:min-h-screen flex flex-col">
                <div className="p-5 border-b border-border">
                    <div className="flex items-center justify-between">
                        <img src="/logo.png" alt="INERRE interior" className="h-10 object-contain" data-testid="admin-logo" />
                        <div className="flex items-center gap-3">
                            <button
                                data-testid="dashboard-refresh-btn"
                                onClick={() => window.location.reload()}
                                className="text-muted-foreground hover:text-foreground transition-colors"
                                title="Muat ulang halaman"
                            >
                                <ArrowClockwise size={20} weight="light" />
                            </button>
                            <a
                                href="/admin"
                                data-testid="admin-scan-link"
                                className="text-muted-foreground hover:text-foreground transition-colors"
                                title="Pindai QR"
                            >
                                <House size={20} weight="light" />
                            </a>
                            <button
                                data-testid="admin-logout-btn"
                                onClick={() => { clearSecret(); setAuthed(false); }}
                                className="text-muted-foreground hover:text-foreground transition-colors"
                                title="Keluar"
                            >
                                <SignOut size={20} weight="light" />
                            </button>
                        </div>
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-2">
                        <button
                            data-testid="reminder-settings-btn"
                            onClick={() => setShowReminders(true)}
                            className="border border-border px-2 py-2 text-xs hover:border-primary hover:text-primary transition-colors flex flex-col items-center gap-1"
                        >
                            <BellRinging size={16} weight="light" /> Pengingat
                        </button>
                        <button
                            data-testid="wa-settings-btn"
                            onClick={() => setShowWaSettings(true)}
                            className="border border-border px-2 py-2 text-xs hover:border-primary hover:text-primary transition-colors flex flex-col items-center gap-1"
                        >
                            <WhatsappLogo size={16} weight="light" /> WhatsApp
                        </button>
                        <button
                            data-testid="cctv-settings-btn"
                            onClick={() => setShowCctvSettings(true)}
                            className="border border-border px-2 py-2 text-xs hover:border-primary hover:text-primary transition-colors flex flex-col items-center gap-1"
                        >
                            <GearSix size={16} weight="light" /> CCTV
                        </button>
                    </div>
                </div>
                <div className="p-5 flex-1">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">Properti</p>
                    <ul className="space-y-1" data-testid="property-list">
                        {properties.map((p) => (
                            <li key={p.id} className="group flex items-center">
                                <button
                                    data-testid={`property-item-${p.id}`}
                                    onClick={() => setSelectedId(p.id)}
                                    className={`flex-1 text-left px-3 py-2 text-sm border transition-colors flex items-center justify-between gap-2 ${
                                        selectedId === p.id
                                            ? "border-primary text-foreground bg-background"
                                            : "border-transparent text-muted-foreground hover:text-foreground"
                                    }`}
                                >
                                    <span className="min-w-0">
                                        <span className="block font-medium">{p.name}</span>
                                        <span className="block text-xs text-muted-foreground">{p.room_count} ruangan</span>
                                    </span>
                                    {(p.warning_count || 0) > 0 && (
                                        <span
                                            data-testid={`property-warning-${p.id}`}
                                            title={`${p.warning_count} ruangan perlu perhatian`}
                                            className="shrink-0 w-5 h-5 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
                                        >
                                            {p.warning_count}
                                        </span>
                                    )}
                                </button>
                                <button
                                    data-testid={`property-delete-${p.id}`}
                                    onClick={() => deleteProperty(p.id)}
                                    className="ml-1 px-2 py-1 text-xs text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity"
                                >
                                    Hapus
                                </button>
                            </li>
                        ))}
                        {properties.length === 0 && (
                            <li className="text-sm text-muted-foreground px-3 py-2">Belum ada properti.</li>
                        )}
                    </ul>
                    <form onSubmit={addProperty} className="mt-4 flex border border-border focus-within:border-primary transition-colors">
                        <input
                            data-testid="new-property-input"
                            value={newProp}
                            onChange={(e) => setNewProp(e.target.value)}
                            placeholder="Nama properti baru"
                            className="flex-1 min-w-0 px-3 py-2 text-sm bg-transparent outline-none"
                        />
                        <button
                            data-testid="add-property-btn"
                            type="submit"
                            disabled={addingProp}
                            className="px-3 text-primary hover:bg-secondary transition-colors"
                        >
                            <Plus size={18} weight="light" />
                        </button>
                    </form>
                </div>
                <div className="p-5 border-t border-border">
                    <a
                        href="/admin/owner"
                        data-testid="dash-owner-link"
                        className="border border-primary text-primary px-3 py-2 text-xs text-center block hover:bg-primary hover:text-primary-foreground transition-colors font-medium"
                    >
                        Dashboard Owner
                    </a>
                </div>
                <div className="p-5 border-t border-border">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2">Checklist Harian</p>
                    <div className="grid grid-cols-2 gap-2">
                        <a
                            href="/admin/checklist/kantor"
                            data-testid="dash-dept-kantor"
                            className="border border-border px-3 py-2 text-xs text-center hover:border-primary hover:text-primary transition-colors"
                        >
                            Kantor INERRE
                        </a>
                        <a
                            href="/admin/checklist/workshop"
                            data-testid="dash-dept-workshop"
                            className="border border-border px-3 py-2 text-xs text-center hover:border-primary hover:text-primary transition-colors"
                        >
                            Workshop INERRE
                        </a>
                    </div>
                </div>
            </aside>

            <main className="flex-1 p-5 md:p-10">
                {!selected && (
                    <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground fade-up">
                        <Buildings size={48} weight="light" />
                        <p className="mt-4 text-sm">Tambahkan properti pertama Anda untuk mulai mengelola ruangan.</p>
                    </div>
                )}
                {selected && (
                    <div className="fade-up" data-testid="rooms-section">
                        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                            <div>
                                <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Properti</p>
                                <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter leading-none font-medium mt-1" data-testid="selected-property-name">
                                    {selected.name}
                                </h1>
                            </div>
                            <form onSubmit={addRoom} className="flex border border-border bg-white focus-within:border-primary transition-colors">
                                <input
                                    data-testid="new-room-input"
                                    value={newRoom}
                                    onChange={(e) => setNewRoom(e.target.value)}
                                    placeholder="Nama ruangan baru"
                                    className="px-3 py-2 text-sm bg-transparent outline-none w-56"
                                />
                                <button
                                    data-testid="add-room-btn"
                                    type="submit"
                                    className="px-4 bg-primary text-primary-foreground text-sm font-medium hover:bg-[#9c4a2a] transition-colors flex items-center gap-1"
                                >
                                    <Plus size={16} weight="bold" /> Tambah
                                </button>
                            </form>
                        </div>

                        {rooms.length === 0 && (
                            <p className="text-sm text-muted-foreground" data-testid="rooms-empty">Belum ada ruangan di properti ini.</p>
                        )}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="rooms-grid">
                            {rooms.map((room) => (
                                <RoomCard key={room.id} room={room} onChanged={refreshRooms} />
                            ))}
                        </div>
                    </div>
                )}
            </main>
            {showCctvSettings && <CctvSettings onClose={() => setShowCctvSettings(false)} />}
            {showWaSettings && <WaSettings onClose={() => setShowWaSettings(false)} />}
            {showReminders && <ReminderSettings onClose={() => setShowReminders(false)} />}
        </div>
    );
}
